import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  SearchIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowUpDownIcon,
  GridIcon,
  ListIcon,
  MoreVerticalIcon,
} from "./Icons";

/**
 * DataTable - A reusable data table component with consistent patterns
 *
 * Features:
 * - Optional search bar
 * - Optional view toggle (table vs cards)
 * - Sortable column headers
 * - Optional row selection with select all
 * - Optional row actions column
 * - Bulk action bar
 * - Empty state
 * - Loading state
 */

// Sort indicator component
function SortIndicator({ field, sortField, sortDirection }) {
  if (sortField !== field) {
    return (
      <span style={{ opacity: 0.3, marginLeft: "4px" }}>
        <ArrowUpDownIcon size={14} />
      </span>
    );
  }
  return (
    <span style={{ marginLeft: "4px" }}>
      {sortDirection === "asc" ? (
        <ArrowUpIcon size={14} />
      ) : (
        <ArrowDownIcon size={14} />
      )}
    </span>
  );
}

// Checkbox component
function Checkbox({ checked, indeterminate, onChange, disabled, ariaLabel }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
    />
  );
}

// Default empty state component
function DefaultEmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="card text-center" style={{ padding: "var(--spacing-2xl)" }}>
      {Icon && (
        <div
          style={{
            fontSize: "4rem",
            marginBottom: "var(--spacing-lg)",
            opacity: 0.5,
          }}
        >
          <Icon size={64} />
        </div>
      )}
      <h3 style={{ marginBottom: "var(--spacing-sm)" }}>{title}</h3>
      {subtitle && (
        <p className="text-muted" style={{ marginBottom: "var(--spacing-lg)" }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// Loading state component
function LoadingState() {
  return (
    <div className="card text-center" style={{ padding: "var(--spacing-2xl)" }}>
      <div className="spinner"></div>
      <p style={{ marginTop: "var(--spacing-md)" }}>Loading...</p>
    </div>
  );
}

// Debounced search input - isolated to prevent re-renders from parent
const DebouncedSearchInput = React.memo(function DebouncedSearchInput({
  placeholder,
  initialValue,
  onDebouncedChange,
  debounceMs = 300,
}) {
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const callbackRef = useRef(onDebouncedChange);
  const [localValue, setLocalValue] = useState(initialValue || "");

  // Keep callback ref up to date without causing re-renders
  callbackRef.current = onDebouncedChange;

  // Sync when the parent changes the search value externally (e.g. clear
  // filters), but don't clobber text the user is mid-typing
  useEffect(() => {
    if (debounceRef.current) return;
    setLocalValue(initialValue || "");
  }, [initialValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalValue(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        callbackRef.current(value);
      }, debounceMs);
    },
    [debounceMs],
  );

  return (
    <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
      <span className="search-icon">
        <SearchIcon size={18} />
      </span>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
      />
    </div>
  );
});

// Bulk action bar component
function BulkActionBar({
  selectedCount,
  totalCount,
  onClearSelection,
  onSelectAll,
  bulkActions,
  showSelectAllOption,
}) {
  return (
    <div className="bulk-action-bar">
      <div className="bulk-action-info">
        <span className="bulk-action-count">{selectedCount} selected</span>
        {showSelectAllOption && (
          <button onClick={onSelectAll} className="btn btn-ghost btn-sm">
            {totalCount ? `Select all ${totalCount}` : "Select all matching"}
          </button>
        )}
        <button onClick={onClearSelection} className="btn btn-ghost btn-sm">
          Clear
        </button>
      </div>
      <div className="bulk-action-buttons">
        {bulkActions.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            className={`btn ${action.variant || "btn-secondary"} btn-sm`}
            disabled={action.disabled}
            title={action.title}
          >
            {action.icon && <action.icon size={16} />}
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * RowActions - Renders row actions as single icon or dropdown menu
 *
 * @param {Array} actions - Array of action objects: { label, icon, onClick, variant, disabled }
 *
 * If 1 action: renders as single icon button
 * If 2+ actions: renders as dropdown menu with "Actions" label
 */
function RowActions({ actions = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Calculate position when opening; flip above the button when the menu
  // would overflow the bottom of the viewport
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const estimatedHeight = actions.length * 37 + 8;
      const openUp = rect.bottom + 4 + estimatedHeight > window.innerHeight;
      setMenuPosition({
        top: openUp
          ? Math.max(8, rect.top - estimatedHeight - 4)
          : rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen, actions.length]);

  // Close on scroll/resize so the fixed-position menu doesn't detach from
  // its row
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => setIsOpen(false);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  if (actions.length === 0) return null;

  // Single action: render as icon button
  if (actions.length === 1) {
    const action = actions[0];
    const Icon = action.icon;
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          action.onClick?.();
        }}
        className="btn btn-ghost btn-icon"
        style={{
          padding: "6px",
          color:
            action.variant === "danger" ? "var(--accent-danger)" : undefined,
        }}
        title={action.label}
        disabled={action.disabled}
      >
        {Icon && <Icon size={16} />}
      </button>
    );
  }

  // Multiple actions: render as dropdown

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="btn btn-ghost btn-icon"
        style={{ padding: "6px" }}
        title="Actions"
      >
        <MoreVerticalIcon size={16} />
      </button>

      {isOpen && (
        <div
          className="dropdown-menu"
          style={{
            position: "fixed",
            top: menuPosition.top,
            right: menuPosition.right,
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-md)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            minWidth: "140px",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          {actions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  action.onClick?.();
                }}
                disabled={action.disabled}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                  padding: "8px 12px",
                  background: "transparent",
                  border: "none",
                  textAlign: "left",
                  cursor: action.disabled ? "not-allowed" : "pointer",
                  color:
                    action.variant === "danger"
                      ? "var(--accent-danger)"
                      : "var(--text-primary)",
                  opacity: action.disabled ? 0.5 : 1,
                  fontSize: "0.875rem",
                }}
                onMouseEnter={(e) => {
                  if (!action.disabled) {
                    e.currentTarget.style.background = "var(--bg-hover)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                {Icon && <Icon size={16} />}
                {action.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Card view component
function CardView({ items, renderCard, columns = 3, onItemClick }) {
  // ~220px min card width at the default 3 columns; a higher requested
  // column count lowers the min width so more columns fit
  const minCardWidth = Math.max(140, Math.floor(660 / columns));
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`,
        gap: "var(--spacing-md)",
      }}
    >
      {items.map((item, index) => (
        <div
          key={item.id || index}
          onClick={() => onItemClick?.(item)}
          style={{ cursor: onItemClick ? "pointer" : "default" }}
        >
          {renderCard(item)}
        </div>
      ))}
    </div>
  );
}

// Main DataTable component
export default function DataTable({
  // Data
  data = [],
  loading = false,
  keyField = "id",

  // Columns configuration
  columns = [],

  // Search
  searchable = false,
  searchPlaceholder = "Search...",
  searchValue, // controlled when provided (with onSearchChange); undefined = uncontrolled
  onSearchChange,
  searchFilter, // (item, query) => boolean

  // Sorting
  sortable = true,
  sortField, // controlled when provided (with onSort); undefined = uncontrolled
  sortDirection,
  onSort,
  defaultSortField = null,
  defaultSortDirection = "desc",

  // Selection
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  selectAllEnabled = true,
  onSelectAllAcrossPages,
  totalItemsCount,

  // View modes
  viewToggle = false,
  viewMode = "table", // "table" or "cards"
  onViewModeChange,
  renderCard,
  cardColumns = 3,

  // Row actions
  rowActions, // (item) => React.ReactNode

  // Bulk actions
  bulkActions = [], // [{ label, onClick, icon, variant, disabled, title }]

  // Empty state
  emptyIcon,
  emptyTitle = "No items found",
  emptySubtitle,

  // Row click
  onRowClick,

  // Load more
  hasMore = false,
  onLoadMore,
  loadMoreLabel = "Load More",
  loadingMore = false,

  // Styling
  stickyHeader = false,
  striped = false,
  compact = false,
  className = "",
}) {
  // Internal state for uncontrolled sorting
  const [internalSortField, setInternalSortField] = useState(
    defaultSortField || (columns[0]?.field ?? null),
  );
  const [internalSortDirection, setInternalSortDirection] =
    useState(defaultSortDirection);
  const [internalSearchValue, setInternalSearchValue] = useState("");

  // Use controlled or uncontrolled state
  const currentSortField = sortField ?? internalSortField;
  const currentSortDirection = sortDirection ?? internalSortDirection;
  const currentSearchValue = searchValue ?? internalSearchValue;

  // Handle sort
  const handleSort = useCallback(
    (field) => {
      if (!sortable) return;

      const newDirection =
        currentSortField === field
          ? currentSortDirection === "asc"
            ? "desc"
            : "asc"
          : "desc";

      if (onSort) {
        onSort(field, newDirection);
      } else {
        setInternalSortField(field);
        setInternalSortDirection(newDirection);
      }
    },
    [sortable, currentSortField, currentSortDirection, onSort],
  );

  // Handle search - called after debounce from DebouncedSearchInput
  const handleSearchChange = useCallback(
    (value) => {
      if (onSearchChange) {
        onSearchChange(value);
      } else {
        setInternalSearchValue(value);
      }
    },
    [onSearchChange],
  );

  // Filter data
  const filteredData = useMemo(() => {
    if (!currentSearchValue || !searchFilter) return data;
    return data.filter((item) => searchFilter(item, currentSearchValue));
  }, [data, currentSearchValue, searchFilter]);

  // Sort data (client-side if no onSort provided)
  const sortedData = useMemo(() => {
    if (onSort || !currentSortField) return filteredData;

    return [...filteredData].sort((a, b) => {
      const column = columns.find((c) => c.field === currentSortField);
      let aVal = a[currentSortField];
      let bVal = b[currentSortField];

      // Use custom sort value getter if provided
      if (column?.getSortValue) {
        aVal = column.getSortValue(a);
        bVal = column.getSortValue(b);
      }

      // Handle different types
      if (typeof aVal === "number" && typeof bVal === "number") {
        return currentSortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (aVal instanceof Date && bVal instanceof Date) {
        return currentSortDirection === "asc"
          ? aVal.getTime() - bVal.getTime()
          : bVal.getTime() - aVal.getTime();
      }

      // String comparison
      const aStr = (aVal || "").toString().toLowerCase();
      const bStr = (bVal || "").toString().toLowerCase();

      if (aStr < bStr) return currentSortDirection === "asc" ? -1 : 1;
      if (aStr > bStr) return currentSortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, currentSortField, currentSortDirection, columns, onSort]);

  // Selection helpers
  const allSelected =
    sortedData.length > 0 &&
    sortedData.every((item) => selectedIds.includes(item[keyField]));
  const someSelected =
    selectedIds.length > 0 &&
    sortedData.some((item) => selectedIds.includes(item[keyField]));
  const indeterminate = someSelected && !allSelected;

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;

    if (allSelected) {
      // Clearing everything (not just the visible rows) is the safe choice:
      // it can't leave an invisible across-pages selection behind.
      onSelectionChange([]);
    } else {
      // Add the visible rows to whatever is already selected rather than
      // replacing it, so a "Select all N" across pages survives a header click.
      const visible = sortedData.map((item) => item[keyField]);
      onSelectionChange([...new Set([...selectedIds, ...visible])]);
    }
  }, [allSelected, sortedData, keyField, selectedIds, onSelectionChange]);

  const handleSelectItem = useCallback(
    (itemId) => {
      if (!onSelectionChange) return;

      if (selectedIds.includes(itemId)) {
        onSelectionChange(selectedIds.filter((id) => id !== itemId));
      } else {
        onSelectionChange([...selectedIds, itemId]);
      }
    },
    [selectedIds, onSelectionChange],
  );

  const handleClearSelection = useCallback(() => {
    onSelectionChange?.([]);
  }, [onSelectionChange]);

  // Determine what content to show
  const showFullLoadingScreen = loading && sortedData.length === 0;
  const showEmptyState = !loading && sortedData.length === 0;
  const showTable = sortedData.length > 0;

  const cellPadding = compact ? "var(--spacing-sm)" : "var(--spacing-md)";

  return (
    <div className={className}>
      {/* Toolbar: Search and View Toggle - ALWAYS rendered in same position */}
      {(searchable || viewToggle) && (
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-md)",
            marginBottom: "var(--spacing-lg)",
            alignItems: "center",
          }}
        >
          {searchable && (
            <DebouncedSearchInput
              placeholder={searchPlaceholder}
              initialValue={currentSearchValue}
              onDebouncedChange={handleSearchChange}
            />
          )}
          {viewToggle && (
            <div className="view-toggle">
              <button
                className={`btn btn-icon ${viewMode === "table" ? "active" : ""}`}
                onClick={() => onViewModeChange?.("table")}
                title="Table view"
              >
                <ListIcon size={18} />
              </button>
              <button
                className={`btn btn-icon ${viewMode === "cards" ? "active" : ""}`}
                onClick={() => onViewModeChange?.("cards")}
                title="Card view"
              >
                <GridIcon size={18} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {showFullLoadingScreen && <LoadingState />}

      {/* Empty state */}
      {showEmptyState && (
        <DefaultEmptyState
          icon={emptyIcon}
          title={currentSearchValue ? "No results found" : emptyTitle}
          subtitle={
            currentSearchValue ? "Try a different search term" : emptySubtitle
          }
        />
      )}

      {/* Card View */}
      {showTable && viewMode === "cards" && renderCard && (
        <CardView
          items={sortedData}
          renderCard={renderCard}
          columns={cardColumns}
          onItemClick={onRowClick}
        />
      )}

      {/* Table View */}
      {showTable && (viewMode === "table" || !renderCard) && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    borderBottom: "1px solid var(--border-color)",
                    background: "var(--bg-secondary)",
                    ...(stickyHeader && {
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }),
                  }}
                >
                  {/* Selection checkbox column */}
                  {selectable && (
                    <th
                      style={{
                        padding: cellPadding,
                        width: "40px",
                        textAlign: "center",
                      }}
                    >
                      {selectAllEnabled && (
                        <Checkbox
                          checked={allSelected}
                          indeterminate={indeterminate}
                          onChange={handleSelectAll}
                          // While a refetch is in flight the rows on screen
                          // belong to the previous filter set; selecting them
                          // would carry hidden rows into the new result.
                          disabled={loading}
                          ariaLabel="Select all"
                        />
                      )}
                    </th>
                  )}

                  {/* Data columns */}
                  {columns.map((column) => {
                    const isSortable = sortable && column.sortable !== false;
                    return (
                    <th
                      key={column.field}
                      onClick={() =>
                        column.sortable !== false && handleSort(column.field)
                      }
                      onKeyDown={(e) => {
                        if (
                          isSortable &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          e.preventDefault();
                          handleSort(column.field);
                        }
                      }}
                      tabIndex={isSortable ? 0 : undefined}
                      aria-sort={
                        isSortable && currentSortField === column.field
                          ? currentSortDirection === "asc"
                            ? "ascending"
                            : "descending"
                          : undefined
                      }
                      style={{
                        padding: cellPadding,
                        textAlign: column.align || "left",
                        cursor: isSortable ? "pointer" : "default",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        whiteSpace: "nowrap",
                        width: column.width,
                        minWidth: column.minWidth,
                      }}
                    >
                      {column.header}
                      {isSortable && (
                        <SortIndicator
                          field={column.field}
                          sortField={currentSortField}
                          sortDirection={currentSortDirection}
                        />
                      )}
                    </th>
                    );
                  })}

                  {/* Actions column */}
                  {rowActions && (
                    <th
                      style={{
                        padding: cellPadding,
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        width: "60px",
                      }}
                    ></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => {
                  const itemId = item[keyField];
                  const isSelected = selectedIds.includes(itemId);

                  return (
                    <tr
                      key={itemId || index}
                      onClick={() => onRowClick?.(item)}
                      onKeyDown={(e) => {
                        if (
                          onRowClick &&
                          (e.key === "Enter" || e.key === " ") &&
                          e.target === e.currentTarget
                        ) {
                          e.preventDefault();
                          onRowClick(item);
                        }
                      }}
                      tabIndex={onRowClick ? 0 : undefined}
                      style={{
                        borderBottom: "1px solid var(--border-color)",
                        cursor: onRowClick ? "pointer" : "default",
                        background: isSelected
                          ? "var(--bg-selected)"
                          : striped && index % 2 === 1
                            ? "var(--bg-secondary)"
                            : undefined,
                      }}
                    >
                      {/* Selection checkbox */}
                      {selectable && (
                        <td
                          style={{
                            padding: cellPadding,
                            textAlign: "center",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectItem(itemId)}
                            disabled={loading}
                            ariaLabel="Select row"
                          />
                        </td>
                      )}

                      {/* Data cells */}
                      {columns.map((column) => (
                        <td
                          key={column.field}
                          style={{
                            padding: cellPadding,
                            textAlign: column.align || "left",
                            ...column.cellStyle,
                          }}
                        >
                          {column.render
                            ? column.render(item[column.field], item)
                            : item[column.field]}
                        </td>
                      ))}

                      {/* Actions cell */}
                      {rowActions && (
                        <td
                          style={{
                            padding: cellPadding,
                            textAlign: "center",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {rowActions(item)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Load More button */}
      {showTable && hasMore && (
        <div style={{ textAlign: "center", marginTop: "var(--spacing-lg)" }}>
          <button
            onClick={onLoadMore}
            className="btn btn-secondary"
            disabled={loadingMore}
          >
            {loadingMore ? "Loading..." : loadMoreLabel}
          </button>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectable && selectedIds.length > 0 && bulkActions.length > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.length}
          totalCount={totalItemsCount}
          onClearSelection={handleClearSelection}
          onSelectAll={onSelectAllAcrossPages}
          bulkActions={bulkActions}
          // With a known total, offer it until everything is selected. Without
          // one (a filtered view, where the page can't cheaply count), offer it
          // whenever more rows exist beyond the loaded pages.
          showSelectAllOption={
            !!onSelectAllAcrossPages &&
            (totalItemsCount
              ? selectedIds.length < totalItemsCount
              : hasMore)
          }
        />
      )}
    </div>
  );
}

// Export sub-components for custom use
export {
  SortIndicator,
  Checkbox,
  BulkActionBar,
  CardView,
  LoadingState,
  RowActions,
};
